<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="file-text" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">File Requests</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end gap-2">
          <button v-if="profile.role !== 'user'" class="btn btn-primary shadow-sm" @click="openFileRequestModal">
            <vue-feather type="file-plus" size="18" class="me-1"></vue-feather>
            Request File
          </button>
          <button class="btn btn-light shadow-sm" @click="$router.go(-1)">
            <vue-feather type="arrow-left" size="18" class="me-1"></vue-feather>
            Back
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="list" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              File Requests ({{fileRequests.length}})
            </div>
            <UInput v-model="searchQuery" size="md" placeholder="Search file requests" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- File Requests Table -->
          <div v-if="!loading && filteredFileRequests.length > 0" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">#</th>
                  <th scope="col" class="fw-semibold">File Name</th>
                  <th scope="col" class="fw-semibold">Description</th>
                  <th scope="col" class="fw-semibold">Requested By</th>
                  <th scope="col" class="fw-semibold">Employees</th>
                  <th scope="col" class="fw-semibold">Pending</th>
                  <th scope="col" class="fw-semibold">Submitted</th>
                  <th scope="col" class="fw-semibold">Created</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(request, index) in paginatedFileRequests" :key="request.id">
                  <td>{{ (page - 1) * perPage + index + 1 }}</td>
                  <td class="fw-medium">{{ request.name }}</td>
                  <td>
                    <span v-if="request.description" class="text-truncate d-inline-block" style="max-width: 200px;" :title="request.description">
                      {{ request.description }}
                    </span>
                    <span v-else class="text-muted fst-italic">—</span>
                  </td>
                  <td>
                    <span v-if="request.user">{{ request.user.first_name }} {{ request.user.last_name }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <span class="badge bg-info-light text-dark">
                      {{ request.employee_file_requests.length }}
                    </span>
                  </td>
                  <td>
                    <span class="badge bg-warning-light text-dark">
                      {{ getStatusCount(request.employee_file_requests, 'pending') }}
                    </span>
                  </td>
                  <td>
                    <span class="badge bg-success-light text-dark">
                      {{ getStatusCount(request.employee_file_requests, 'submitted') }}
                    </span>
                  </td>
                  <td>{{ formatDate(request.created_at) }}</td>
                  <td class="text-end">
                    <button
                      class="btn btn-sm btn-outline-primary"
                      @click="viewRequest(request)"
                    >
                      <vue-feather type="eye" size="16"></vue-feather>
                      View
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && filteredFileRequests.length === 0" class="text-center py-4">
            <div class="empty-state">
              <div class="bg-light p-4 rounded-circle mb-3 d-inline-block">
                <vue-feather type="file-text" size="32" stroke="#6c757d"></vue-feather>
              </div>
              <h5>No File Requests Found</h5>
              <p class="text-muted">File requests will appear here once created</p>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="!loading && filteredFileRequests.length > 0" class="pagination-container d-flex flex-wrap justify-content-around pt-3 pb-4">
            <div class="col-md-3">
              <button class="btn btn-outline-dark text-uppercase shadow-sm" @click="$router.go(-1)">
                <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
                Back
              </button>
            </div>
            <div class="col-md-6 text-center">
              <p class="mb-2 small">Rows per page</p>
              <select class="form-select shadow-sm mx-auto rows-per-page" v-model="perPage">
                <option v-for="item in [5, 10, 25, 50]" :key="item" :value="item">{{item}}</option>
              </select>
            </div>
            <div class="col-md-3">
              <Pagination v-model="page" :pages="Math.ceil(filteredFileRequests.length / perPage)">
                <template v-slot:next>
                  Next
                </template>
                <template v-slot:previous>
                  Prev
                </template>
              </Pagination>
            </div>
          </div>
        </Card>
      </div>
    </div>

    <!-- View Request Modal -->
    <Modal :open="viewModal" @closed="viewModal = false" dialog-size="modal-lg">
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 fw-bold">
          <vue-feather type="file-text" size="20" class="me-2"></vue-feather>
          File Request Details
        </h5>
        <button @click="viewModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <div v-if="selectedRequest">
          <!-- Request Info -->
          <div class="card bg-light border-0 mb-4">
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="small text-muted mb-1">File Name</label>
                  <div class="fw-semibold">{{ selectedRequest.name }}</div>
                </div>
                <div class="col-md-4">
                  <label class="small text-muted mb-1">Requested By</label>
                  <div class="fw-semibold">
                    <span v-if="selectedRequest.user">{{ selectedRequest.user.first_name }} {{ selectedRequest.user.last_name }}</span>
                    <span v-else class="text-muted">—</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="small text-muted mb-1">Description</label>
                  <div>
                    <span v-if="selectedRequest.description">{{ selectedRequest.description }}</span>
                    <span v-else class="text-muted fst-italic">No description provided</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="small text-muted mb-1">Requested On</label>
                  <div>{{ formatDate(selectedRequest.created_at) }}</div>
                </div>
                <div class="col-md-4">
                  <label class="small text-muted mb-1">Total Employees</label>
                  <div>
                    <span class="badge bg-info-light text-dark">
                      {{ selectedRequest.employee_file_requests.length }}
                    </span>
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="small text-muted mb-1">Status Summary</label>
                  <div>
                    <span class="badge bg-warning-light text-dark me-1">
                      {{ getStatusCount(selectedRequest.employee_file_requests, 'pending') }} Pending
                    </span>
                    <span class="badge bg-success-light text-dark">
                      {{ getStatusCount(selectedRequest.employee_file_requests, 'submitted') }} Submitted
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Employees List -->
          <div>
            <h6 class="mb-3">
              <vue-feather type="users" size="18" class="me-2"></vue-feather>
              Employees ({{ selectedRequest.employee_file_requests.length }})
            </h6>
            <div class="table-responsive">
              <table class="table table-sm table-hover">
                <thead class="table-light">
                  <tr>
                    <th scope="col">Employee No</th>
                    <th scope="col">Name</th>
                    <th scope="col">Phone</th>
                    <th scope="col">Status</th>
                    <th scope="col">Submitted On</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="empRequest in selectedRequest.employee_file_requests" :key="empRequest.id">
                    <td>{{ empRequest.employee.employee_no }}</td>
                    <td>{{ empRequest.employee.full_name }}</td>
                    <td>{{ empRequest.employee.phone }}</td>
                    <td>
                      <span class="badge" :class="getStatusBadgeClass(empRequest.status)">
                        {{ empRequest.status }}
                      </span>
                    </td>
                    <td>
                      <span v-if="empRequest.submitted_at">{{ formatDate(empRequest.submitted_at) }}</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer border-0 pt-0">
        <button
          type="button"
          class="btn btn-secondary"
          @click="viewModal = false"
        >
          Close
        </button>
      </div>
    </Modal>

    <!-- Request Files Modal -->
    <RequestFilesModal
      :open="fileRequestModal"
      :employees="employees"
      @closed="fileRequestModal = false"
      @success="handleFileRequestSuccess"
    />
  </div>
</template>

<script>
import fileRequestService from "@/api/fileRequestService";
import employeeService from "@/api/employeeService";
import formatDate from "@/utils/formatDate";
import {decrypt} from "@/utils/crypto";
import VueFeather from 'vue-feather';
import RequestFilesModal from '@/components/modals/RequestFilesModal.vue';

export default {
  name: "FileRequests",
  components: { VueFeather, RequestFilesModal },

  data() {
    return {
      profile: {},
      loading: true,
      fileRequests: [],
      employees: [],
      searchQuery: '',
      page: 1,
      perPage: 10,
      viewModal: false,
      selectedRequest: null,
      fileRequestModal: false,
    }
  },

  computed: {
    filteredFileRequests() {
      if (!this.searchQuery) {
        return this.fileRequests;
      }
      const term = this.searchQuery.toLowerCase();
      return this.fileRequests.filter(request =>
        request.name.toLowerCase().includes(term) ||
        (request.description && request.description.toLowerCase().includes(term)) ||
        (request.user && `${request.user.first_name} ${request.user.last_name}`.toLowerCase().includes(term))
      );
    },
    paginatedFileRequests() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredFileRequests.slice(start, start + this.perPage);
    }
  },

  methods: {
    formatDate,
    async getEmployees() {
      try {
        const allEmployees = await employeeService.getEmployees();
        this.employees = allEmployees.filter(emp => emp.status === 'active');
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    },
    async getFileRequests() {
      this.loading = true;
      try {
        this.fileRequests = await fileRequestService.getFileRequests();
      } catch (error) {
        console.error("Error fetching file requests:", error);
      } finally {
        this.loading = false;
      }
    },
    viewRequest(request) {
      this.selectedRequest = request;
      this.viewModal = true;
    },
    openFileRequestModal() {
      this.fileRequestModal = true;
    },
    async handleFileRequestSuccess() {
      this.fileRequestModal = false;
      await this.getFileRequests();
    },
    getStatusCount(employeeRequests, status) {
      return employeeRequests.filter(req => req.status === status).length;
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'pending':
          return 'bg-warning text-dark';
        case 'submitted':
          return 'bg-success';
        case 'rejected':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    await this.getEmployees();
    await this.getFileRequests();
  },

  watch: {
    searchQuery() {
      this.page = 1;
    },
    perPage() {
      this.page = 1;
    }
  }
}
</script>

<style scoped>
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.1);
}

.text-primary {
  color: #652aa3 !important;
}

.empty-state {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.font-size-125 {
  font-size: 1.25rem;
}

.color-gray-600 {
  color: #6c757d;
}

.search-input {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.search-input:focus-within {
  box-shadow: 0 2px 10px rgba(101, 42, 163, 0.15);
}

.btn {
  transition: all 0.2s ease;
}

.btn:hover:not(.disabled) {
  transform: translateY(-2px);
}

.badge {
  padding: 0.5em 0.75em;
}

.rows-per-page {
  width: 80px;
  margin: 0 auto;
}

.bg-info-light {
  background-color: rgba(13, 202, 240, 0.1);
}

.bg-warning-light {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-success-light {
  background-color: rgba(25, 135, 84, 0.1);
}
</style>
